import type { ComponentType } from 'react';
import styled from 'styled-components';
import {
  Box,
  dangerouslyReadGlobalThemeValues,
  Stack,
  Text,
  theme,
  useHasMounted,
  usePrevious,
} from '@frameio/vapor';
import type { IconProps } from '@frameio/vapor-icons';
import {
  animateBackground,
  animateContent,
  animateDone,
  getBackgroundStyles,
  getContentStyles,
  getDoneStyles,
} from './animations';
import { ConnectProgressBar } from './ConnectProgressBar';

const { palette } = dangerouslyReadGlobalThemeValues;

export type DisplayStates = 'BLANK' | 'LOOPING' | 'DONE' | 'ERROR';

type ConnectDisplayItems = {
  label?: string | null;
  Icon?: ComponentType<IconProps>;
};

type ConnectDisplayProps = {
  contextIcon: ConnectDisplayItems;
  destinationIcon: ConnectDisplayItems;
  doneIcon?: ConnectDisplayItems;
  errorIcon?: ConnectDisplayItems;
  dimensions: {
    paddingTop: number;
    height: number;
  };
  state?: DisplayStates;
  sourceIcon: ConnectDisplayItems;
};

const CONTEXT_WIDTH_HEIGHT = '36px';
const CONTEXT_TOP_MARGIN = '14px';

/**
 * Returns a visual representation of connecting "source" to "destination"
 * Visualizes the connection between two services.
 *
 * This provides context to the user
 * but does not provide a way to interact with the connection.
 *
 * [ source ] -> [ context ] -> [ destination ]
 *
 * Context icon can be replaced with Done and Error icons
 * when state changes and icons are provided
 *
 * @param dimensions - The dimensions of the display
 * @param sourceIcon - The source of the connection (icon should be 64x64)
 * @param contextIcon - The context of the connection (icon should be 36x36)
 * @param destinationIcon - The destination of the connection (icon should be 64x64)
 * @param doneIcon - (optional) The icon to display when the complete
 * @param errorIcon - (optional) The icon to display when there is an error
 */
export const ConnectDisplay = ({
  contextIcon,
  destinationIcon,
  dimensions: { paddingTop, height },
  doneIcon,
  errorIcon,
  state = 'LOOPING',
  sourceIcon,
}: ConnectDisplayProps) => {
  const hasMounted = useHasMounted();
  const previousMounted = usePrevious(hasMounted) ?? hasMounted;

  const DestinationIcon = destinationIcon.Icon;
  const SourceIcon = sourceIcon.Icon;
  const ContextIcon = contextIcon.Icon;
  const DoneIcon = doneIcon?.Icon;
  const ErrorIcon = errorIcon?.Icon;

  const showDone = state === 'DONE';
  const showError = state === 'ERROR';
  const showContext = !(showDone || showError);

  return (
    <ContentBox $height={height} $paddingTop={paddingTop}>
      <ConnectProgressBar state={state === 'LOOPING' ? 'REPEAT' : 'BLANK'} />
      <ContentGrid>
        <Stack alignX="center" gap={8}>
          <IconContainer>
            <Box data-chromatic="ignore" display="flex">
              {SourceIcon && <SourceIcon color={palette.grey[100]} size={24} />}
            </Box>
          </IconContainer>
          <TextContainer>
            <Text
              color="tertiary"
              textAlign="center"
              truncate={2}
              variant="caption"
            >
              {sourceIcon.label}
            </Text>
          </TextContainer>
        </Stack>
        <ContextSpacingContainer data-testid={'context container'}>
          <ContextCircle
            $show={showContext}
            style={getContentStyles({
              show: showContext,
              mounted: previousMounted,
            })}
          >
            <CircleBackground
              $background={theme.colors.background.component}
              $show={showContext}
              style={getBackgroundStyles({
                show: showContext,
                mounted: previousMounted,
              })}
            />
            <CircleContent data-chromatic="ignore">
              {ContextIcon && (
                <ContextIcon color={theme.colors.icon.primary} size={24} />
              )}
            </CircleContent>
          </ContextCircle>
          {DoneIcon && (
            <DoneCircle
              $show={showDone}
              style={getDoneStyles({
                show: showDone,
                mounted: previousMounted,
              })}
            >
              <CircleBackground
                $background={theme.colors.meta.mediumOpacity.green}
                $show={showDone}
                style={getBackgroundStyles({
                  show: showDone,
                  mounted: previousMounted,
                })}
              />
              <CircleContent data-chromatic="ignore">
                <DoneIcon color={theme.colors.meta.icon.green} size={24} />
              </CircleContent>
            </DoneCircle>
          )}
          {ErrorIcon && (
            <ErrorCircle
              $show={showError}
              style={getDoneStyles({
                show: showError,
                mounted: previousMounted,
              })}
            >
              <CircleBackground
                $background={theme.colors.meta.mediumOpacity.red}
                $show={showError}
                style={getBackgroundStyles({
                  show: showError,
                  mounted: previousMounted,
                })}
              />
              <CircleContent data-chromatic="ignore">
                <ErrorIcon color={theme.colors.meta.icon.red} size={24} />
              </CircleContent>
            </ErrorCircle>
          )}
        </ContextSpacingContainer>
        <Stack alignX="center" gap={8}>
          <IconContainer>
            <Box data-chromatic="ignore" display="flex">
              {DestinationIcon && (
                <DestinationIcon color={palette.grey[100]} size={24} />
              )}
            </Box>
          </IconContainer>
          <TextContainer>
            <Text
              color="tertiary"
              textAlign="center"
              truncate={2}
              variant="caption"
            >
              {destinationIcon.label}
            </Text>
          </TextContainer>
        </Stack>
      </ContentGrid>
    </ContentBox>
  );
};

const ContentBox = styled(Box)<{ $paddingTop: number; $height: number }>`
  align-items: flex-start;
  display: flex;
  height: ${(p) => p.$height}px;
  justify-content: center;
  padding-top: ${(p) => p.$paddingTop}px;
  position: relative;
  width: 100%;
`;

/**
 * The content of the modal is fixed utilize pixel constants to
 * position content accordingly.
 */
const ContentGrid = styled(Box)`
  display: grid;
  grid-template-columns: 100px 100px 100px;
  justify-items: center;
  padding-top: ${0}px;
  position: relative;
`;

const TextContainer = styled.div`
  width: 100px;
`;

const ContextSpacingContainer = styled.div`
  // Make sure this rests above the ConnectProgressBar.
  z-index: 1;
  align-items: center;
  display: flex;
  height: ${CONTEXT_WIDTH_HEIGHT};
  justify-content: center;
  width: ${CONTEXT_WIDTH_HEIGHT};
  margin-top: ${CONTEXT_TOP_MARGIN};
`;

const IconContainer = styled.div`
  align-items: center;
  background-color: ${palette.grey[8]};
  border: 2px solid ${theme.colors.border.secondary};
  border-radius: ${theme.radii[8]};
  display: flex;
  height: ${theme.spaces[64]};
  justify-content: center;
  overflow: hidden;
  width: ${theme.spaces[64]};
`;

const CircleContainer = styled.div`
  align-items: center;
  display: flex;
  height: ${CONTEXT_WIDTH_HEIGHT};
  justify-content: center;
  position: absolute;
  width: ${CONTEXT_WIDTH_HEIGHT};
  z-index: 1;
`;

const CircleBackground = styled.div<{
  $background?: string;
  $show: boolean;
}>`
  /**
   * theme.colors.background.component is transparent 
   * so add solid background to hide the progress bar.
   */
  &:before {
    background-color: ${palette.grey[10]};
    border: 1px solid ${palette.grey[10]};
    border-radius: 100%;
    content: '';
    height: 100%;
    left: -1px;
    position: absolute;
    top: -1px;
    width: 100%;
  }
  &:after {
    background-color: ${(p) => p.$background};
    border: 1px solid ${(p) => p.$background};
    border-radius: 100%;
    content: '';
    height: 100%;
    left: -1px;
    position: absolute;
    top: -1px;
    width: 100%;
  }

  animation: ${(p) => (p.$show ? animateBackground.in : animateBackground.out)};

  height: 100%;
  position: absolute;
  width: 100%;
`;

const CircleContent = styled.div`
  display: flex;
  z-index: 1;
`;

const ContextCircle = styled(CircleContainer)<{ $show: boolean }>`
  animation-name: ${(p) => (p.$show ? animateContent.in : animateContent.out)};
`;

const DoneCircle = styled(CircleContainer)<{ $show: boolean }>`
  animation-name: ${(p) => (p.$show ? animateDone.in : animateDone.out)};
`;

const ErrorCircle = styled(CircleContainer)<{ $show: boolean }>`
  animation-name: ${(p) => (p.$show ? animateDone.in : animateDone.out)};
`;
